import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import {
  API_BASE,
  LiveBoardRow,
  LiveBoardWsMessage,
  PenaltyRow,
  PenaltyWsMessage,
  PitRow,
  PitWsMessage,
  parsePitTimeSeconds,
} from './scoring-types';
import { StatusChip } from './StatusChip';
import useWebSocketHandler from '../wsHookHandler';
import ConvertTime from '../converters/timestampConverter';

const CHECKED_ROW_BG = 'rgba(255, 193, 7, 0.18)';
const CHECKED_ROW_HOVER_BG = 'rgba(255, 193, 7, 0.28)';
const CHECKBOX_YELLOW = '#fbc02d';

// ---------------------------------------------------------------------------
// Tab 1 — On-track summary
// ---------------------------------------------------------------------------
function OnTrackTable({
  rows,
  emptyMessage = 'No cars on the board yet — waiting for race activity.',
}: {
  rows: LiveBoardRow[];
  emptyMessage?: string;
}) {
  const headers = [
    'Car', 'Institution', 'Laps', 'Status', 'DC Laps', 'Pit Laps', 'Penalties',
    'Transponder', 'UID',
  ];

  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {headers.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 600, bgcolor: 'dimgrey', color: 'white' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={headers.length} align="center" sx={{ color: 'text.secondary' }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => (
              <TableRow key={r.CarNumber} hover>
                <TableCell>{r.CarNumber}</TableCell>
                <TableCell>{r.Institution}</TableCell>
                <TableCell>{r.OnTrackLaps}</TableCell>
                <TableCell><StatusChip carNumber={r.CarNumber} status={r.Status} /></TableCell>
                <TableCell>{r.DCLaps}</TableCell>
                <TableCell>{r.PitLaps}</TableCell>
                <TableCell>{r.PenaltyCount}</TableCell>
                <TableCell>{r.Transponder ?? ''}</TableCell>
                <TableCell>{r.UID ?? ''}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Pits
// ---------------------------------------------------------------------------

function PitsTable({
  rows,
  onEndClick,
}: {
  rows: PitRow[];
  onEndClick: (row: PitRow) => void;
}) {
  const [checkedRows, setCheckedRows] = useState<Set<number>>(() => new Set());
  const headers = [
    'Car', 'Institution', 'Type', 'DC #',
    'Start', 'End', 'Pit Time (s)',
    'Free?', 'Excess (s)', 'Restart (s)',
    '', // actions
    'Check',
  ];
  const toggleChecked = (recnum: number) => {
    setCheckedRows((current) => {
      const next = new Set(current);
      if (next.has(recnum)) {
        next.delete(recnum);
      } else {
        next.add(recnum);
      }
      return next;
    });
  };

  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {headers.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 600, bgcolor: 'dimgrey', color: 'white' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={headers.length} align="center" sx={{ color: 'text.secondary' }}>
                No pit events recorded.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => {
              const isOpen = r.EndTime == null;
              const isChecked = checkedRows.has(r.Recnum);
              return (
                <TableRow
                  key={r.Recnum}
                  hover
                  sx={[
                    isOpen && { bgcolor: 'rgba(255, 193, 7, 0.12)' },
                    isChecked && {
                      bgcolor: CHECKED_ROW_BG,
                      '&:hover': { bgcolor: CHECKED_ROW_HOVER_BG },
                    },
                  ]}
                >
                  <TableCell>{r.CarNumber}</TableCell>
                  <TableCell>{r.Institution}</TableCell>
                  <TableCell>
                    {r.PitType}
                    {isOpen && (
                      <Chip
                        label="OPEN"
                        size="small"
                        sx={{ ml: 1, bgcolor: 'goldenrod', color: 'white', fontWeight: 600 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{r.PitNumber ?? ''}</TableCell>
                  <TableCell>{ConvertTime(r.StartTime)}</TableCell>
                  <TableCell>{ConvertTime(r.EndTime)}</TableCell>
                  <TableCell>{isOpen ? '—' : parseFloat(r.PitTime).toFixed(1)}</TableCell>
                  <TableCell>{isOpen ? '' : (r.IsFree ? 'yes' : 'no')}</TableCell>
                  <TableCell>{parseFloat(r.ExcessTime).toFixed(1)}</TableCell>
                  <TableCell>{r.RestartTime ? parseFloat(r.RestartTime).toFixed(1) : ''}</TableCell>
                  <TableCell align="right">
                    {isOpen && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={() => onEndClick(r)}
                      >
                        End Pit
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="center" padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={() => toggleChecked(r.Recnum)}
                      sx={{
                        '&.Mui-checked': { color: CHECKBOX_YELLOW },
                      }}
                      inputProps={{ 'aria-label': `Highlight pit row ${r.Recnum}` }}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Penalties
// ---------------------------------------------------------------------------
function PenaltiesTable({ rows }: { rows: PenaltyRow[] }) {
  const [checkedRows, setCheckedRows] = useState<Set<number>>(() => new Set());
  const headers = ['Car', 'Institution', 'Type', 'Lap', 'Description', 'Time (s)', 'Points', 'When', 'Check'];
  const toggleChecked = (recnum: number) => {
    setCheckedRows((current) => {
      const next = new Set(current);
      if (next.has(recnum)) {
        next.delete(recnum);
      } else {
        next.add(recnum);
      }
      return next;
    });
  };

  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {headers.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 600, bgcolor: 'dimgrey', color: 'white' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={headers.length} align="center" sx={{ color: 'text.secondary' }}>
                No penalties recorded.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => {
              const isChecked = checkedRows.has(r.Recnum);
              return (
                <TableRow
                  key={r.Recnum}
                  hover
                  sx={
                    isChecked
                      ? {
                          bgcolor: CHECKED_ROW_BG,
                          '&:hover': { bgcolor: CHECKED_ROW_HOVER_BG },
                        }
                      : undefined
                  }
                >
                  <TableCell>{r.CarNumber}</TableCell>
                  <TableCell>{r.Institution}</TableCell>
                  <TableCell>{r.PenaltyType}</TableCell>
                  <TableCell>{r.LapNumber ?? ''}</TableCell>
                  <TableCell>{r.Description ?? ''}</TableCell>
                  <TableCell>{parseFloat(r.TimePenalty).toFixed(1)}</TableCell>
                  <TableCell>{parseFloat(r.PointPenalty).toFixed(0)}</TableCell>
                  <TableCell>{ConvertTime(r.CreatedAt)}</TableCell>
                  <TableCell align="center" padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={() => toggleChecked(r.Recnum)}
                      sx={{
                        '&.Mui-checked': { color: CHECKBOX_YELLOW },
                      }}
                      inputProps={{ 'aria-label': `Highlight penalty row ${r.Recnum}` }}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ---------------------------------------------------------------------------
// End-pit dialog — invoked from the "End Pit" button on open rows. Same
// payload as EventCard's Pit — End events, just a quicker entry path.
// ---------------------------------------------------------------------------
const DC_ALLOWED_TIME_SEC = 180;
const DC_RESTART_TIME_SEC = 120;

function resolveDcPitTime(rawValue: string, isOverMode: boolean): number {
  const trimmed = rawValue.trim();
  if (!trimmed) return DC_ALLOWED_TIME_SEC;
  const parsed = parsePitTimeSeconds(trimmed);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Could not parse "${rawValue}" as seconds or m:ss`);
  }
  return isOverMode ? DC_ALLOWED_TIME_SEC + parsed : parsed;
}

function resolveRestartTime(rawValue: string, isOverMode: boolean): number | null {
  const trimmed = rawValue.trim();
  if (!trimmed) return null;
  const parsed = parsePitTimeSeconds(trimmed);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Could not parse "${rawValue}" as seconds or m:ss`);
  }
  return isOverMode ? DC_RESTART_TIME_SEC + parsed : parsed;
}

function EndPitDialog({
  pit,
  onClose,
}: {
  pit: PitRow | null;
  onClose: () => void;
}) {
  const [pitTime, setPitTime] = useState('');
  const [restartTime, setRestartTime] = useState('');
  const [pitTimeOverAllowance, setPitTimeOverAllowance] = useState(false);
  const [restartTimeOverAllowance, setRestartTimeOverAllowance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset fields whenever the dialog opens on a new pit.
  useEffect(() => {
    setPitTime('');
    setRestartTime('');
    setPitTimeOverAllowance(false);
    setRestartTimeOverAllowance(false);
    setErr(null);
  }, [pit?.Recnum]);

  if (!pit) return null;
  const isDC = pit.PitType === 'DC';
  const parsedPitTime = parsePitTimeSeconds(pitTime);
  const parsedRestartTime = parsePitTimeSeconds(restartTime);
  const pitTimeValid = pitTime.trim() === '' ? isDC : Number.isFinite(parsedPitTime);
  const restartTimeValid = restartTime.trim() === '' || Number.isFinite(parsedRestartTime);
  const canSubmit = pitTimeValid && (!isDC || restartTimeValid) && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      const resolvedPitTime = isDC
        ? resolveDcPitTime(pitTime, pitTimeOverAllowance)
        : parsedPitTime;
      const body: Record<string, unknown> = {
        CarNumber: pit.CarNumber,
        PitType:   pit.PitType,
        PitTime:   resolvedPitTime,
      };
      if (isDC) {
        const restart = resolveRestartTime(restartTime, restartTimeOverAllowance);
        if (restart != null) body.RestartTime = restart;
      }
      await axios.post(`${API_BASE}/api/scoring/pits/end`, body);
      onClose();
    } catch (e) {
      const message =
        axios.isAxiosError(e) && e.response?.data
          ? typeof e.response.data === 'string'
            ? e.response.data
            : (e.response.data as { error?: string }).error ?? JSON.stringify(e.response.data)
          : (e as Error).message;
      setErr(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!pit} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        End {pit.PitType}
        {pit.PitNumber ? ` ${pit.PitNumber}` : ''} — Car {pit.CarNumber}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Started {ConvertTime(pit.StartTime)} · {pit.Institution}
          </Typography>
          <TextField
            autoFocus
            label={isDC && pitTimeOverAllowance ? 'DC Time Over Allowance' : isDC ? 'DC Time' : 'Official Pit Time'}
            size="small"
            value={pitTime}
            onChange={(e) => setPitTime(e.target.value)}
            helperText={
              pitTime.trim() !== '' && !Number.isFinite(parsedPitTime)
                ? `Could not parse "${pitTime}" — use seconds (175) or m:ss (2:55)`
                : isDC && pitTimeOverAllowance
                  ? `Seconds or m:ss past the ${DC_ALLOWED_TIME_SEC}s allowance`
                  : isDC
                    ? `Seconds or m:ss. Blank = within ${DC_ALLOWED_TIME_SEC}s allowance.`
                    : 'Seconds or m:ss (radioed in)'
            }
            error={pitTime.trim() !== '' && !Number.isFinite(parsedPitTime)}
            required={!isDC}
            fullWidth
          />
          {isDC && (
            <FormControlLabel
              sx={{ mt: -1 }}
              control={
                <Switch
                  size="small"
                  checked={pitTimeOverAllowance}
                  onChange={(_e, checked) => {
                    setPitTimeOverAllowance(checked);
                    setPitTime('');
                  }}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Enter time over allowance
                </Typography>
              }
            />
          )}
          {isDC && (
            <>
              <TextField
                label={restartTimeOverAllowance ? 'Restart Time Over Allowance' : 'Restart Time'}
                size="small"
                value={restartTime}
                onChange={(e) => setRestartTime(e.target.value)}
                helperText={
                  restartTime.trim() !== '' && !Number.isFinite(parsedRestartTime)
                    ? `Could not parse "${restartTime}" — use seconds (120) or m:ss (2:00)`
                    : restartTimeOverAllowance
                      ? `Seconds or m:ss past the ${DC_RESTART_TIME_SEC}s allowance`
                      : `Seconds or m:ss. Blank = within ${DC_RESTART_TIME_SEC}s allowance.`
                }
                error={restartTime.trim() !== '' && !Number.isFinite(parsedRestartTime)}
                fullWidth
              />
              <FormControlLabel
                sx={{ mt: -1 }}
                control={
                  <Switch
                    size="small"
                    checked={restartTimeOverAllowance}
                    onChange={(_e, checked) => {
                      setRestartTimeOverAllowance(checked);
                      setRestartTime('');
                    }}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Enter time over allowance
                  </Typography>
                }
              />
            </>
          )}
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={!canSubmit}
        >
          {submitting ? 'Ending…' : 'End Pit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
type AnyWsMessage =
  | { channel: 'liveBoard';    message: LiveBoardWsMessage }
  | { channel: 'allCarsBoard'; message: LiveBoardWsMessage }
  | { channel: 'pit';          message: PitWsMessage }
  | { channel: 'penalty';      message: PenaltyWsMessage };

// Tab indices. "Cars On Track" is the default so the operator lands
// on the tab they use most; "All Cars" is a super-set accessible one
// click away for a whole-field overview (including cars that haven't
// crossed the loop yet).
const TAB_ALL_CARS     = 0;
const TAB_ON_TRACK     = 1;
const TAB_PITS         = 2;
const TAB_PENALTIES    = 3;
const TAB_FINISHED     = 4;
const DEFAULT_TAB      = TAB_ON_TRACK;
const FINISHED_STATUSES = new Set(['off track', 'finished', 'dnf', 'dq']);

function isFinishedStatus(status: unknown): boolean {
  return FINISHED_STATUSES.has(String(status ?? '').trim().toLowerCase());
}

export default function LiveBoard() {
  const [tab, setTab] = useState<number>(DEFAULT_TAB);
  const [boardRows,    setBoardRows]    = useState<LiveBoardRow[]>([]);
  const [allCarsRows,  setAllCarsRows]  = useState<LiveBoardRow[]>([]);
  const [pitRows,      setPitRows]      = useState<PitRow[]>([]);
  const [penaltyRows,  setPenaltyRows]  = useState<PenaltyRow[]>([]);
  const [loadError,    setLoadError]    = useState<string | null>(null);
  const [endingPit,    setEndingPit]    = useState<PitRow | null>(null);
  const finishedRows = allCarsRows.filter((row) => isFinishedStatus(row.Status));

  // Initial snapshot fetch — websocket keeps us in sync after this.
  // Both `liveBoard` (race-active cars) and `allCarsBoard` (full field)
  // are kept fresh via their own ws channels; the backend publishes both
  // in a single broadcast call per event, so they never drift.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [b, all, p, pen] = await Promise.all([
          axios.get<LiveBoardRow[]>(`${API_BASE}/api/scoring/liveBoard`),
          axios.get<LiveBoardRow[]>(`${API_BASE}/api/scoring/allCarsBoard`),
          axios.get<PitRow[]>      (`${API_BASE}/api/scoring/allPits`),
          axios.get<PenaltyRow[]>  (`${API_BASE}/api/scoring/allPenalties`),
        ]);
        if (cancelled) return;
        setBoardRows(b.data);
        setAllCarsRows(all.data);
        setPitRows(p.data);
        setPenaltyRows(pen.data);
      } catch (err) {
        if (!cancelled) setLoadError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Merge incoming websocket messages into local state.
  const onWsMessage = useCallback((msg: AnyWsMessage) => {
    switch (msg.channel) {
      case 'liveBoard': {
        const { CarNumber, row } = msg.message;
        setBoardRows((prev) => {
          const without = prev.filter((r) => r.CarNumber !== CarNumber);
          if (!row) return without; // null row => drop from on-track view
          return [...without, row].sort((a, b) => a.CarNumber - b.CarNumber);
        });
        break;
      }
      case 'allCarsBoard': {
        // Full-field view: status / transponder / penalty changes on
        // zero-lap cars land here even when 'liveBoard' publishes null.
        // A null row means the car no longer exists in Cars (rare —
        // deletion / renumber), so we drop it.
        const { CarNumber, row } = msg.message;
        setAllCarsRows((prev) => {
          const without = prev.filter((r) => r.CarNumber !== CarNumber);
          if (!row) return without;
          return [...without, row].sort((a, b) => a.CarNumber - b.CarNumber);
        });
        break;
      }
      case 'pit': {
        if (msg.message.action === 'upsert') {
          const row = msg.message.row;
          setPitRows((prev) => {
            const without = prev.filter((p) => p.Recnum !== row.Recnum);
            return [row, ...without];
          });
        } else {
          const { Recnum } = msg.message;
          setPitRows((prev) => prev.filter((p) => p.Recnum !== Recnum));
        }
        break;
      }
      case 'penalty': {
        if (msg.message.action === 'upsert') {
          const row = msg.message.row;
          setPenaltyRows((prev) => {
            const without = prev.filter((p) => p.Recnum !== row.Recnum);
            return [row, ...without];
          });
        } else {
          const { Recnum } = msg.message;
          setPenaltyRows((prev) => prev.filter((p) => p.Recnum !== Recnum));
        }
        break;
      }
    }
  }, []);

  useWebSocketHandler<AnyWsMessage['message']>(
    ['liveBoard', 'allCarsBoard', 'pit', 'penalty'],
    (m) => onWsMessage(m as AnyWsMessage),
  );

  return (
    <Paper sx={{ m: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`All Cars (${allCarsRows.length})`} />
        <Tab label={`Cars On Track (${boardRows.length})`} />
        <Tab label={`Pits (${pitRows.length})`} />
        <Tab label={`Penalties (${penaltyRows.length})`} />
        <Tab label={`Finished (${finishedRows.length})`} />
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loadError && (
          <Typography color="error" sx={{ p: 2 }}>
            Failed to load initial data: {loadError}
          </Typography>
        )}
        {tab === TAB_ALL_CARS  && <OnTrackTable rows={allCarsRows} emptyMessage="No cars configured." />}
        {tab === TAB_ON_TRACK  && <OnTrackTable rows={boardRows} />}
        {tab === TAB_PITS      && <PitsTable rows={pitRows} onEndClick={setEndingPit} />}
        {tab === TAB_PENALTIES && <PenaltiesTable rows={penaltyRows} />}
        {tab === TAB_FINISHED  && (
          <OnTrackTable
            rows={finishedRows}
            emptyMessage="No cars have finished or left the race yet."
          />
        )}
      </Box>

      <EndPitDialog pit={endingPit} onClose={() => setEndingPit(null)} />
    </Paper>
  );
}
